import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system/helpers/types';

import { MAX_OWNED_HOMES } from 'src/constants';
import { user_data } from 'src/stores/account/user';
import { currentUnitId, homes } from 'src/stores/account/home';
import { isSubAccount } from 'src/helpers/user_helpers';
import type { Home, HomeAvatar, HomePlan } from 'src/types/Home';
import type {
	GetUnitsInfoResponse,
	OwnUnitInfo,
	SubUnitInfo,
	UnitAvatar,
} from 'src/types/responses/getUnitsInfoResponse';

/**
 * Resolve the translated default home name (used when the cloud returns
 * an empty/whitespace `name`). Mirrors `fillMissingUnitNames` in
 * `utilities.ts` so both code paths agree on the user-visible fallback.
 * @deprecated - This function is deprecated and will be removed in the future.
 */
const defaultHomeName = (): string => get(format)('settings_default_home_name', { default: 'My Home' });

/**
 * Get the number of owned homes.
 * @param availableUnits - The available units.
 * @returns The number of owned homes.
 */
export const getOwnedHomeCount = (availableUnits: Record<string, string> | null | undefined): number =>
	Object.keys(availableUnits ?? {}).length;

/**
 * Owned-home slot usage for the MAX_OWNED_HOMES limit.
 * Incoming pending transfers occupy a slot on the recipient (FE-2027).
 * Outgoing pending stays in `available_units`, so it is already counted.
 */
export const getOwnedHomeSlotCount = (
	availableUnits: Record<string, string> | null | undefined,
	incomingPendingCount = 0,
): number => getOwnedHomeCount(availableUnits) + Math.max(0, incomingPendingCount);

/**
 * Check if the user has any owned units.
 * @param availableUnits - The available units.
 * @returns True if the user has any owned units, false otherwise.
 */
export const hasOwnedUnit = (availableUnits: Record<string, string> | null | undefined): boolean =>
	getOwnedHomeCount(availableUnits) > 0;

/**
 * Check if the user can add an owned home.
 * @param availableUnits - The available units.
 * @param incomingPendingCount - Incoming pending transfers that reserve a slot.
 * @returns True if the user can add an owned home, false otherwise.
 */
export const canAddOwnedHome = (
	availableUnits: Record<string, string> | null | undefined,
	incomingPendingCount = 0,
): boolean => getOwnedHomeSlotCount(availableUnits, incomingPendingCount) < MAX_OWNED_HOMES;

const planFromPremium = (premium: boolean): HomePlan => (premium ? 'premium' : 'standard');

const normalizeName = (name: string | undefined | null): string => name?.trim() || defaultHomeName();

/**
 * The cloud's `avatar` field on `/user/get_units_info` is polymorphic
 * (see `UnitAvatar` for the documented shape). Normalise into the
 * single `Home.avatar` object so the UI can hand the values
 * straight to `RectangularAvatar` without re-detecting.
 *
 * Accepts:
 * - `null` / `undefined` → `undefined`
 * - String starting with `color-`/`gradient-` → `{ backgroundColor }`
 * - Any other string → `{ image }` (treated as URL)
 * - Object → pass through, picking only the recognised keys
 */
const isColorToken = (value: string): boolean =>
	value.startsWith('color-') || value.startsWith('gradient-');

const parseAvatar = (raw: UnitAvatar | string | null | undefined): HomeAvatar | undefined => {
	if (raw == null) return undefined;

	if (typeof raw === 'string') {
		if (raw.length === 0) return undefined;
		if (isColorToken(raw)) {
			return { backgroundColor: raw as AvatarColorVariantTypes };
		}
		return { image: raw };
	}

	if (typeof raw === 'object') {
		const result: HomeAvatar = {};
		if (typeof raw.text === 'string' && raw.text.length > 0) {
			result.text = raw.text;
		}
		if (typeof raw.image === 'string' && raw.image.length > 0) {
			result.image = raw.image;
		}
		if (typeof raw.backgroundColor === 'string' && raw.backgroundColor.length > 0) {
			result.backgroundColor = raw.backgroundColor as AvatarColorVariantTypes;
		}
		return Object.keys(result).length > 0 ? result : undefined;
	}

	return undefined;
};

const mapUnitCommon = (entry: OwnUnitInfo): Pick<Home, 'id' | 'name' | 'avatar' | 'plan'> => ({
	id: String(entry.unit_id),
	name: normalizeName(entry.name),
	avatar: parseAvatar(entry.avatar),
	plan: planFromPremium(entry.premium),
});

// `unit_id` in `/user/get_units_info` is `number`, but `currentUnitId`
// is persisted as a `string` (see `house-management.ts`). We normalise
// to string on both sides of the comparison so identity isn't lost to
// strict-equality (`4296636 === "4296636"` → `false`).

const mapOwnUnit = (entry: OwnUnitInfo, currentUnitId: string | undefined): Home => ({
	...mapUnitCommon(entry),
	isActive: !!currentUnitId && String(entry.unit_id) === currentUnitId,
	isShared: false,
});

const mapSubUnit = (entry: SubUnitInfo, currentUnitId: string | undefined): Home => ({
	...mapUnitCommon(entry),
	sub: entry.sub_id,
	isActive: !!currentUnitId && String(entry.unit_id) === currentUnitId,
	isShared: true,
	sharedByEmail: entry.shared_by?.trim() || undefined,
});

/**
 * Map the raw `/user/get_units_info` response into the `Home[]`
 * shape consumed by the dropdown.
 *
 * @param response - Raw response from `getUnitsInfo()`. The cloud wraps
 *   the payload in `{ isok, data: { own_units, sub_units } }`.
 * @param currentUnitId - Active `unit_id` for the session, taken from
 *   the `currentUnitId` store. Globally unique across `own_units` and
 *   `sub_units`, so a single match identifies the active home in both
 *   owned and shared sessions.
 */
export const mapHomesResponse = (
	response: GetUnitsInfoResponse,
	currentUnitId: string | undefined,
): Home[] => {
	const payload = response.data;
	const owned = (payload?.own_units ?? []).map((entry) => mapOwnUnit(entry, currentUnitId));
	const shared = (payload?.sub_units ?? []).map((entry) => mapSubUnit(entry, currentUnitId));
	return [...owned, ...shared];
};

/**
 * Find an owned (non-shared) home by id. Falls back to a minimal `Home`
 * built from the `available_units` map when the homes list hasn't loaded
 * yet, so callers (e.g. the edit modal) always get a usable object.
 */
export const findOwnedHome = (
	list: Home[],
	id: string,
	availableUnits: Record<string, string>,
	activeUnitId: string,
): Home =>
	list.find((entry) => entry.id === id && !entry.isShared) ?? {
		id,
		name: availableUnits[id] ?? '',
		plan: 'standard',
		isActive: id === activeUnitId,
		isShared: false,
	};

/**
 * Build a `Home` for a not-yet-created home (e.g. the add-home preview or the
 * edit-name/avatar modal preview). An empty avatar selection is dropped so the
 * avatar falls back to the icon.
 */
export const buildPendingHome = (
	name: string,
	avatar?: HomeAvatar,
	plan: HomePlan = 'standard',
): Home => ({
	id: '',
	name,
	avatar: avatar?.image || avatar?.backgroundColor || avatar?.text ? avatar : undefined,
	plan,
	isActive: false,
	isShared: false,
});

const sortByName = (a: Home, b: Home): number =>
	a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * Group helper used by the dropdown to render the chip + the two
 * sections. Active is excluded from `owned` to avoid duplication, since
 * it's already shown as the panel's first row in the
 * `positionOverTrigger` layout.
 */
export const getHomeGroups = (
	list: Home[],
	options?: { excludeUnitIds?: Iterable<number> },
): { active: Home | undefined; owned: Home[]; shared: Home[] } => {
	const active = list.find((home) => home.isActive);
	const exclude = new Set(options?.excludeUnitIds ?? []);
	const owned = list
		.filter((home) => !home.isShared && home.id !== active?.id && !exclude.has(Number(home.id)))
		.sort(sortByName);
	const shared = list
		.filter((home) => home.isShared && (home.id !== active?.id || home.sub !== active?.sub))
		.sort(sortByName);
	return { active, owned, shared };
};

export const getProfileDisplayName = (
	title: string | null | undefined,
	email: string,
	unitId?: string | number | null,
): string => {
	const trimmed = title?.trim() ?? '';
	// The cloud sometimes returns the raw unit_id / user_id as the account name for
	// never-renamed accounts (e.g. right after registration). Never surface that —
	// fall back to the account email per the standing display rule.
	if (!trimmed || (unitId != null && trimmed === String(unitId).trim())) {
		return email;
	}
	return trimmed;
};

export const getHomeDisplayName = (rawName: string | null | undefined, ownerEmail: string): string =>
	rawName?.trim() || ownerEmail;

const hasDistinctProfileTitle = (title: string | null | undefined, email: string): boolean => {
	const trimmedTitle = title?.trim() ?? '';
	return trimmedTitle.length > 0 && trimmedTitle !== email;
};

/** Secondary line for profile cards when the title differs from the account email. */
export const getProfileEmailSubtitle = (title: string | null | undefined, email: string): string =>
	hasDistinctProfileTitle(title, email) ? email : '';

/** Single-line label for profile pickers (radio lists): name, or "name (email)" when both add context. */
export const getProfileSelectionLabel = (title: string | null | undefined, email: string): string => {
	const displayName = getProfileDisplayName(title, email);

	if (hasDistinctProfileTitle(title, email)) {
		return `${displayName} (${email})`;
	}

	return displayName;
};

export const isUnnamedDefaultHome = (
	rawName: string | null | undefined,
	defaultHomeLabel = defaultHomeName(),
	unitId?: string | number | null,
): boolean => {
	const trimmed = rawName?.trim() ?? '';
	if (trimmed.length === 0) return true;
	// The cloud sometimes stores the raw unit_id as the home name for never-renamed
	// homes. Treat that as unnamed so profile-name inheritance can kick in.
	if (unitId != null && trimmed === String(unitId).trim()) return true;
	return trimmed.localeCompare(defaultHomeLabel, undefined, { sensitivity: 'accent' }) === 0;
};

export const resolveUniqueHomeName = (proposed: string, existingNames: string[]): string => {
	const normalizedExisting = new Set(
		existingNames.map((name) => name.trim().toLowerCase()).filter(Boolean),
	);
	const base = proposed.trim() || 'Home';

	if (!normalizedExisting.has(base.toLowerCase())) {
		return base;
	}

	let suffix = 2;
	while (normalizedExisting.has(`${base} (${suffix})`.toLowerCase())) {
		suffix += 1;
	}

	return `${base} (${suffix})`;
};

/**
 * Whether the active session home is a shared (sub-unit) home rather than an owned home.
 * Used to disable features that only apply when the user can add devices to their account.
 */
export const isActiveHomeShared = (homesList?: Home[]): boolean => {
	const list = homesList ?? get(homes).list;
	const active = list.find((home) => home.isActive);

	if (active) {
		return active.isShared;
	}

	const unitId = get(currentUnitId);
	if (!unitId) {
		return false;
	}

	const subUnits = get(user_data)?.available_sub_units ?? {};
	return Object.prototype.hasOwnProperty.call(subUnits, unitId);
};

/** Whether the user can add, edit, or delete home-level entities on the active home. */
export const canManageHomeEntities = (homesList?: Home[]): boolean =>
	!isActiveHomeShared(homesList) && !isSubAccount();
